import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import { FiPhone } from "react-icons/fi";
import { showToast } from "../../utils/customToast";
import {
  normalizeMobile,
  generateSecureOTP,
  checkMobileExists,
  sendWhatsappOtp,
} from "./authHelper";
import "./ForgotPassword.css";

function ForgotPassword() {
  const [mobile, setMobile] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const navigate = useNavigate();

  const handleMobileChange = (e) => {
    const val = e.target.value;
    setMobile(val);
    if (errorMessage) setErrorMessage("");
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (loading) return;

    setErrorMessage("");

    const cleanMobile = normalizeMobile(mobile);
    if (!cleanMobile || cleanMobile.length !== 10) {
      setErrorMessage("Please enter a valid 10-digit registered mobile number.");
      showToast.warn("Please enter a valid 10-digit mobile number.");
      return;
    }

    setLoading(true);

    try {
      // 1. Verify if user exists in database (handles +91, 91, or raw 10 digits)
      const checkResult = await checkMobileExists(cleanMobile);

      if (!checkResult.exists) {
        setErrorMessage(
          checkResult.message ||
            "This mobile number is not registered with us. Please check and try again."
        );
        showToast.error("Mobile number is not registered.");
        setLoading(false);
        return;
      }

      // 2. Generate random 4-digit OTP
      const otp = generateSecureOTP();
      const expiresAt = Date.now() + 5 * 60 * 1000; // 5 minutes expiration

      console.log(`[DEV MODE] Generated 4-Digit OTP for ${cleanMobile}:`, otp);

      // 3. WhatsApp API - Send OTP
      const sendResult = await sendWhatsappOtp(cleanMobile, otp);

      if (sendResult && (sendResult.success === true || sendResult.apiResponse)) {
        showToast.success("4-digit OTP sent to your WhatsApp number!");

        // 4. Navigate to OTP verification page with session state
        navigate("/verify-otp", {
          state: {
            mobile: cleanMobile,
            generatedOtp: otp,
            expiresAt,
            user: checkResult.user || null,
          },
        });
      } else {
        const msg =
          sendResult?.message ||
          "Could not send OTP via WhatsApp. Please check the number and try again.";
        setErrorMessage(msg);
        showToast.error(msg);
      }
    } catch (err) {
      console.error("Forgot password error:", err);
      setErrorMessage("An unexpected error occurred. Please try again later.");
      showToast.error("Failed to process request. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        {/* Brand Logo */}
        <div className="auth-logo">
          <img src="/media/response_info.jpg" alt="Response Infoway" />
        </div>

        {/* Step Progress Indicator */}
        <div className="auth-steps-indicator">
          <div className="auth-step-dot active" title="Step 1: Mobile Number"></div>
          <div className="auth-step-dot" title="Step 2: OTP Verification"></div>
          <div className="auth-step-dot" title="Step 3: Reset Password"></div>
        </div>

        {/* Card Header */}
        <div className="auth-header">
          <h2 className="auth-title">Forgot Password?</h2>
          <p className="auth-subtitle">
            Enter your registered mobile number and we'll send a 4-digit OTP to your WhatsApp.
          </p>
        </div>

        {/* Error Alert */}
        {errorMessage && (
          <div className="auth-alert-error">
            <span>{errorMessage}</span>
          </div>
        )}

        {/* Form */}
        <form className="auth-form" onSubmit={handleSendOtp}>
          <div className="auth-form-group">
            <label className="auth-form-label" htmlFor="forgot-mobile">
              Registered Mobile Number <span className="required">*</span>
            </label>
            <div className="mobile-input-group">
              <span className="mobile-prefix-badge">
                <FiPhone /> +91
              </span>
              <input
                id="forgot-mobile"
                type="tel"
                className="mobile-input-field"
                placeholder="Enter 10-digit number"
                value={mobile}
                onChange={handleMobileChange}
                maxLength={14}
                autoFocus
                required
              />
            </div>
          </div>

          <button
            type="submit"
            className="auth-btn-primary"
            disabled={loading}
          >
            {loading ? (
              <span className="btn-loading-wrapper">
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                <span>Checking & Sending OTP...</span>
              </span>
            ) : (
              "Send OTP via WhatsApp"
            )}
          </button>
        </form>

        {/* Footer Back Link */}
        <div className="auth-footer">
          <Link to="/" className="auth-back-link">
            <FaArrowLeft /> Back to Login
          </Link>
        </div>
      </div>
    </div>
  );
}

export default ForgotPassword;
